'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

const STATUS_LABEL = {
  drafting: 'Drafting',
  'gaps-pending': 'Gaps Pending',
  'internal-review': 'Internal Review',
  shared: 'Shared',
  'changes-requested': 'Changes Requested',
  approved: 'Approved',
}

const STATUS_COLOR = {
  drafting: 'var(--theme-elevation-500)',
  'gaps-pending': 'var(--theme-warning-500, #b26a00)',
  'internal-review': 'var(--theme-elevation-500)',
  shared: 'var(--theme-elevation-500)',
  'changes-requested': 'var(--theme-warning-500, #b26a00)',
  approved: 'var(--theme-success-500, #2e7d32)',
}

const PORTAL_VISIBLE_STATUSES = ['shared', 'changes-requested', 'approved']

const CONFIDENCE_COLOR = {
  high: 'var(--theme-success-500, #2e7d32)',
  medium: 'var(--theme-warning-500, #b26a00)',
  low: 'var(--theme-elevation-500)',
}

// Mirrors src/lib/prd/import.ts's MAX_IMPORT_MARKDOWN_BYTES — client-side
// pre-check so an oversize file gets a friendly inline error instead of
// making a round trip just to hit the server's 413.
const MAX_IMPORT_BYTES = 200 * 1024

// Mirrors the import-docx route's own 10MB cap (CD Task 4) — same
// client-side friendly-error-before-round-trip idea as MAX_IMPORT_BYTES
// above, sized for a docx (binary, so a much larger cap than the plain-text
// markdown one).
const MAX_DOCX_IMPORT_BYTES = 10 * 1024 * 1024

const SECTION_LABELS = {
  problem: 'Problem',
  personas: 'Personas',
  solution: 'Solution',
  'mvp-scope': 'MVP Scope',
  'non-goals': 'Non-Goals',
  'user-stories': 'User Stories',
  'technical-approach': 'Technical Approach',
  milestones: 'Milestones',
  'success-metrics': 'Success Metrics',
  risks: 'Risks',
}

// True when `proposedPlan` exists and has not yet been applied — either no
// plan was ever applied, or this proposal is strictly newer than the last
// apply (proposedAt vs planAppliedAt, both ISO strings — see
// src/lib/prd/plan.ts's claimPlanApply for the same comparison server-side).
function isPlanUnapplied(prd) {
  const plan = prd?.proposedPlan
  if (!plan || typeof plan !== 'object' || Array.isArray(plan) || !plan.proposedAt) return false
  if (!prd.planAppliedAt) return true
  return new Date(plan.proposedAt).getTime() > new Date(prd.planAppliedAt).getTime()
}

// Admin intake controls (Task 4) — worded straight from the fields
// (locked/completedAt/reopenedAt/status), never assumed from the PRD's
// lifecycle status. This is also the persistent, always-visible replacement
// for the old transient "An intake is out — regenerating will replace the
// founder's remaining questions" warning that only fired for
// sent/in-progress: that copy went stale the moment a founder revised an
// already-complete intake (writeGapAnswer flips intake.status back to
// 'in-progress' on a review-screen edit) and never covered the
// complete-but-unlocked case at all (live-until-locked model — a complete
// intake is still open for revision until locked).
function intakeStatusLine(prd) {
  const intake = prd?.intake
  if (!intake?.token || !intake?.status || intake.status === 'not-sent') {
    return 'Intake: not sent'
  }
  // Reopened/reset wins over everything else as long as no completion has
  // happened SINCE the stamp — checked BEFORE the 'complete' branch below on
  // purpose: Reopen leaves intake.status at 'complete' untouched (only
  // locked/reopenedAt/nudge counters change), so keying off status alone
  // would never surface this bucket for a freshly-reopened intake. Reset
  // also stamps reopenedAt (with completedAt cleared to null), so a reset
  // intake reads as "reopened — awaiting founder" too, same as a plain
  // reopen — the confirm dialog and email already carry the "wiped clean"
  // distinction; this line just needs to say the founder has something to
  // do. A fresh confirm moves completedAt past reopenedAt and falls through.
  if (intake.reopenedAt && (!intake.completedAt || new Date(intake.reopenedAt) > new Date(intake.completedAt))) {
    return `Intake: reopened ${new Date(intake.reopenedAt).toLocaleDateString()} — awaiting founder`
  }
  if (intake.status === 'complete') {
    return intake.locked ? 'Intake: complete — locked' : 'Intake: complete — unlocked (founder can still edit)'
  }
  if (intake.status === 'in-progress') return 'Intake: in progress'
  return 'Intake: sent'
}

// Mounted (via PrdPanel.jsx) on the Projects edit view. Shows the project's
// latest PRD — status, version, gap count — and the full generate → intake
// → review → plan action set. Only Generate/Regenerate are backed by a
// shipped endpoint today (T3); Send intake / Share / Propose plan post to
// T4–T6 endpoints that don't exist yet and simply surface a friendly
// "not available yet" toast on 404 — the panel ships with the full button
// set now so nothing needs rework once those tasks land.
export function PrdPanelClient({ projectId }) {
  const [loading, setLoading] = useState(true)
  const [prd, setPrd] = useState(null)
  const [portalToken, setPortalToken] = useState(null)
  const [loadError, setLoadError] = useState(null)
  const [actionBusy, setActionBusy] = useState(null)
  const [actionMessage, setActionMessage] = useState(null)

  // Markdown import (Task 9) — PREVIEW (agent-mapped, nothing written) then
  // CONFIRM (writes through the same generate.ts claim/version machinery).
  // See src/lib/prd/import.ts / PrdPanel.jsx binding contract #4.
  const importFileRef = useRef(null)
  const [importOpen, setImportOpen] = useState(false)
  const [importFileName, setImportFileName] = useState(null)
  const [importPreview, setImportPreview] = useState(null) // { mapping, previewHash }
  const [importLifecycle, setImportLifecycle] = useState('internal-review')
  // The operator's own gap-analysis preference — persists across a
  // lifecycle toggle so switching back off 'approved' restores it. The
  // value actually SENT to preview/confirm is `importEffectiveRunGaps`
  // below (forced off while lifecycle is 'approved').
  const [importGapsWanted, setImportGapsWanted] = useState(true)
  const [importBusy, setImportBusy] = useState(null) // 'preview' | 'confirm' | null
  const [importError, setImportError] = useState(null)
  const importEffectiveRunGaps = importLifecycle === 'approved' ? false : importGapsWanted

  // Client PRD-revision submissions (CD Task 4) — unprocessed client-files
  // rows (kind='prd-revision', processedAt null) for this project, newest
  // first. `importSubmissionId` tracks which submission (if any) the
  // currently-open import modal is reviewing, so confirmImport can pass it
  // through for post-confirm stamping — null for the plain direct-upload
  // import flow (unchanged).
  const [submissions, setSubmissions] = useState([])
  const [importSubmissionId, setImportSubmissionId] = useState(null)
  const [submissionBusyId, setSubmissionBusyId] = useState(null)

  const loadState = useCallback(async () => {
    setLoading(true)
    setLoadError(null)
    try {
      const [prdRes, projectRes, submissionsRes] = await Promise.all([
        fetch(
          `/payload-api/prds?where[project][equals]=${encodeURIComponent(projectId)}&sort=-version&limit=1&depth=0`,
          { credentials: 'include' },
        ),
        fetch(`/payload-api/projects/${encodeURIComponent(projectId)}?depth=0`, {
          credentials: 'include',
        }),
        fetch(
          `/payload-api/client-files?where[project][equals]=${encodeURIComponent(projectId)}&where[kind][equals]=prd-revision&where[processedAt][exists]=false&sort=-createdAt&depth=0`,
          { credentials: 'include' },
        ),
      ])
      if (!prdRes.ok) throw new Error(`Failed to load PRDs (HTTP ${prdRes.status})`)
      const prdData = await prdRes.json()
      const projectData = projectRes.ok ? await projectRes.json() : null
      const submissionsData = submissionsRes.ok ? await submissionsRes.json() : null
      setPrd(prdData?.docs?.[0] || null)
      setPortalToken(projectData?.portalToken || null)
      setSubmissions(submissionsData?.docs || [])
    } catch (err) {
      setLoadError(err.message)
    } finally {
      setLoading(false)
    }
  }, [projectId])

  useEffect(() => {
    loadState()
  }, [loadState])

  async function callEndpoint(url, body) {
    setActionBusy(url)
    setActionMessage(null)
    try {
      const res = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      let data = null
      try {
        data = await res.json()
      } catch {
        /* non-JSON error body (e.g. a bare 404) — handled below */
      }
      if (!res.ok || !data?.ok) {
        const text = data?.busy
          ? data?.error || 'Another action is already in progress for this PRD — try again shortly.'
          : data?.invalid
            ? data?.error || "This PRD isn't in a state where that action is available."
            : data?.reason === 'no-founder-email'
              ? 'No founder email on file — add a client contact on the project (or link a Z2H application/agreement), then try again.'
              : res.status === 404
                ? 'Not available yet — this action ships in a later task.'
                : data?.error || `HTTP ${res.status}`
        setActionMessage({ ok: false, text })
        return null
      }
      setActionMessage({ ok: true, text: 'Done.' })
      return data
    } catch (err) {
      setActionMessage({ ok: false, text: err.message })
      return null
    } finally {
      setActionBusy(null)
    }
  }

  function cancelImport() {
    setImportOpen(false)
    setImportFileName(null)
    setImportPreview(null)
    setImportLifecycle('internal-review')
    setImportGapsWanted(true)
    setImportError(null)
    setImportBusy(null)
    setImportSubmissionId(null)
  }

  async function runImportPreview(markdown, runGaps) {
    setImportBusy('preview')
    setImportError(null)
    setImportPreview(null)
    try {
      const res = await fetch('/api/z2h/prd/import/preview', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectId, markdown, runGaps }),
      })
      let data = null
      try {
        data = await res.json()
      } catch {
        /* non-JSON error body */
      }
      if (!res.ok || !data?.ok) {
        setImportError(
          res.status === 413
            ? data?.error || 'Markdown file is too large.'
            : data?.error || `HTTP ${res.status}`,
        )
        return
      }
      setImportPreview({ mapping: data.mapping, previewHash: data.previewHash })
    } catch (err) {
      setImportError(err.message)
    } finally {
      setImportBusy(null)
    }
  }

  // Import PRD → hidden file input → .md/.markdown/.txt is read locally
  // (client cap 200KB) and posted as text to /import/preview; .docx (CD
  // Task 4) is NEVER read as text client-side — it's posted multipart to
  // import-docx, which converts it server-side and returns the SAME preview
  // shape. Either way the result lands in the same importPreview state and
  // the same modal below. Re-selecting the SAME file re-triggers onChange
  // (the input's value is reset every time) so a corrected re-upload works.
  function openImportPicker() {
    importFileRef.current?.click()
  }

  // Shared by both docx entry points (direct file picker upload AND
  // "Review & import" on a submission) — posts multipart to import-docx and
  // lands the result in the same importPreview/importError state the
  // markdown path uses.
  async function runDocxImportPreview(formFields, runGaps) {
    setImportBusy('preview')
    setImportError(null)
    setImportPreview(null)
    try {
      const form = new FormData()
      for (const [key, value] of Object.entries(formFields)) form.append(key, value)
      form.append('runGaps', String(runGaps))
      const res = await fetch(`/api/z2h/prd/${encodeURIComponent(projectId)}/import-docx`, {
        method: 'POST',
        credentials: 'include',
        body: form,
      })
      let data = null
      try {
        data = await res.json()
      } catch {
        /* non-JSON error body */
      }
      if (!res.ok || !data?.ok) {
        setImportError(data?.error || `HTTP ${res.status}`)
        return
      }
      setImportPreview({ mapping: data.mapping, previewHash: data.previewHash })
    } catch (err) {
      setImportError(err.message)
    } finally {
      setImportBusy(null)
    }
  }

  async function handleImportFile(e) {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    if (/\.docx$/i.test(file.name)) {
      if (file.size > MAX_DOCX_IMPORT_BYTES) {
        setImportOpen(true)
        setImportPreview(null)
        setImportFileName(null)
        setImportError(`File is too large (max ${Math.floor(MAX_DOCX_IMPORT_BYTES / (1024 * 1024))}MB).`)
        return
      }
      setImportOpen(true)
      setImportFileName(file.name)
      setImportError(null)
      setImportSubmissionId(null)
      await runDocxImportPreview({ file }, importGapsWanted)
      return
    }
    if (!/\.(md|markdown|txt)$/i.test(file.name)) {
      setImportOpen(true)
      setImportPreview(null)
      setImportFileName(null)
      setImportError('Please choose a .md, .markdown, .txt, or .docx file.')
      return
    }
    if (file.size > MAX_IMPORT_BYTES) {
      setImportOpen(true)
      setImportPreview(null)
      setImportFileName(null)
      setImportError(`File is too large (max ${Math.floor(MAX_IMPORT_BYTES / 1024)}KB).`)
      return
    }
    const markdown = await file.text()
    setImportOpen(true)
    setImportFileName(file.name)
    setImportError(null)
    setImportSubmissionId(null)
    await runImportPreview(markdown, importGapsWanted)
  }

  // "Review & import" on a client submission (CD Task 4) — same modal, same
  // import-docx endpoint, `submissionId` instead of `file`; the resulting
  // preview flows through the EXISTING confirm below, which — because
  // importSubmissionId is now set — passes submissionId through so confirm
  // stamps the submission row on success.
  async function reviewSubmission(submission) {
    setImportOpen(true)
    setImportPreview(null)
    setImportError(null)
    setImportFileName(submission.note || 'Client-submitted revision')
    setImportSubmissionId(submission.id)
    await runDocxImportPreview({ submissionId: String(submission.id) }, importGapsWanted)
  }

  async function dismissSubmission(submission) {
    if (!window.confirm('Dismiss this submission without importing it? This cannot be undone.')) {
      return
    }
    setSubmissionBusyId(submission.id)
    try {
      const res = await fetch(`/api/z2h/prd/submission/${submission.id}/dismiss`, {
        method: 'POST',
        credentials: 'include',
      })
      let data = null
      try {
        data = await res.json()
      } catch {
        /* non-JSON error body */
      }
      if (!res.ok || !data?.ok) {
        setActionMessage({ ok: false, text: data?.error || `HTTP ${res.status}` })
        return
      }
      setActionMessage({ ok: true, text: 'Submission dismissed.' })
      await loadState()
    } catch (err) {
      setActionMessage({ ok: false, text: err.message })
    } finally {
      setSubmissionBusyId(null)
    }
  }

  async function confirmImport() {
    if (!importPreview) return
    setImportBusy('confirm')
    setImportError(null)
    try {
      const res = await fetch('/api/z2h/prd/import/confirm', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          projectId,
          mapping: importPreview.mapping,
          previewHash: importPreview.previewHash,
          lifecycle: importLifecycle,
          runGaps: importEffectiveRunGaps,
          ...(importSubmissionId ? { submissionId: importSubmissionId } : {}),
        }),
      })
      let data = null
      try {
        data = await res.json()
      } catch {
        /* non-JSON error body */
      }
      if (!res.ok || !data?.ok) {
        setImportError(data?.error || `HTTP ${res.status}`)
        return
      }
      setActionMessage({
        ok: true,
        text: `${data.created ? 'Imported' : 'Re-imported'} v${data.version} — ${STATUS_LABEL[data.status] || data.status}${
          data.gapCount ? `, ${data.gapCount} gap${data.gapCount === 1 ? '' : 's'} to resolve` : ''
        }.`,
      })
      cancelImport()
      await loadState()
    } catch (err) {
      setImportError(err.message)
    } finally {
      setImportBusy(null)
    }
  }

  // Extra warning clauses folded onto the base regenerate confirm copy when
  // regenerating would disrupt something currently in flight (review fix,
  // Critical #1c + Minor #6 — no code-path change, the server-side guards
  // already hold; this is purely making the consequence visible before the
  // admin clicks). At most one applies in practice (an intake goes out well
  // before a share), but check both so neither silently drops if that ever
  // changes.
  function regenerateConfirmCopy() {
    const base =
      'Regenerate this PRD? It will be re-derived from the source data (plus any answered gaps and review feedback already on file) — do this once you know something new should be reflected.'
    // The old "an intake is out" warning lived here, gated on intake.status
    // === 'sent'/'in-progress' — subsumed by the always-visible intake
    // status line (see intakeStatusLine above), which the admin already sees
    // before ever opening this confirm dialog, and which correctly covers
    // the complete-but-unlocked case that condition always missed.
    const warnings = []
    if (status === 'shared' || status === 'changes-requested') {
      warnings.push('A founder review is open — regenerating will close it until you re-share.')
    }
    return warnings.length ? `${base} ${warnings.join(' ')}` : base
  }

  async function generate(isRegenerate) {
    if (isRegenerate && !window.confirm(regenerateConfirmCopy())) {
      return
    }
    const data = await callEndpoint('/api/z2h/prd/generate', { projectId })
    if (data) {
      setActionMessage({
        ok: true,
        text: `${data.created ? 'Generated' : 'Regenerated'} v${data.version} — ${STATUS_LABEL[data.status] || data.status}${
          data.gapCount ? `, ${data.gapCount} gap${data.gapCount === 1 ? '' : 's'} to resolve` : ''
        }.`,
      })
      await loadState()
    }
  }

  async function sendIntake() {
    if (!prd) return
    const data = await callEndpoint('/api/z2h/prd/intake/send', { prdId: prd.id })
    if (data) await loadState()
  }

  async function shareWithFounder() {
    if (!prd) return
    const data = await callEndpoint('/api/z2h/prd/share', { prdId: prd.id })
    if (data) {
      // Important #3 fold-in: the route now claims (status → shared,
      // intake.locked → true) BEFORE sending the founder email, so a failed
      // send no longer rolls the state back — it just can't stamp
      // sharedAt/lastSharedSnapshot. Surface that distinctly (same
      // emailSent-branching precedent as reopenIntake/resetIntake above)
      // rather than letting callEndpoint's generic "Done." mask a failed
      // send.
      setActionMessage({
        ok: !data.emailFailed,
        text: data.emailFailed
          ? 'Shared — the draft is locked in, but the founder email failed to send. Resend once the issue is fixed.'
          : 'Shared — the founder has been emailed.',
      })
      await loadState()
    }
  }

  // Admin intake controls (Task 4): Reopen keeps every answer, Reset wipes
  // them all, Lock closes the intake for good until an admin reopens it.
  async function reopenIntake() {
    if (!prd) return
    if (!window.confirm("Reopen this intake so the founder can revisit their answers? Nothing they've already told us is lost.")) {
      return
    }
    const data = await callEndpoint('/api/z2h/prd/intake/reopen', { prdId: prd.id })
    if (data) {
      setActionMessage({
        ok: true,
        text: data.emailSent
          ? 'Reopened — the founder has been emailed.'
          : 'Reopened — no founder email on file, so nothing was sent.',
      })
      await loadState()
    }
  }

  async function resetIntake() {
    if (!prd) return
    if (
      !window.confirm(
        "Reset this intake? This clears ALL the founder's answers — they'll start over from scratch. This cannot be undone.",
      )
    ) {
      return
    }
    const data = await callEndpoint('/api/z2h/prd/intake/reset', { prdId: prd.id })
    if (data) {
      setActionMessage({
        ok: true,
        text: data.emailSent
          ? 'Reset — the founder has been emailed to start fresh.'
          : 'Reset — no founder email on file, so nothing was sent.',
      })
      await loadState()
    }
  }

  async function lockIntake() {
    if (!prd) return
    if (
      !window.confirm(
        'Lock this intake? The founder will no longer be able to revise their answers until you reopen it.',
      )
    ) {
      return
    }
    const data = await callEndpoint('/api/z2h/prd/intake/lock', { prdId: prd.id })
    if (data) await loadState()
  }

  async function proposePlan() {
    if (!prd) return
    const data = await callEndpoint('/api/z2h/prd/propose-plan', { prdId: prd.id })
    if (data) {
      setActionMessage({
        ok: true,
        text: `Proposed ${data.mode === 'delta' ? 'a delta ' : ''}plan — ${data.epicCount} epic${data.epicCount === 1 ? '' : 's'}, ${data.sprintCount} sprint${data.sprintCount === 1 ? '' : 's'}, ${data.taskCount} task${data.taskCount === 1 ? '' : 's'}.`,
      })
      await loadState()
    }
  }

  async function applyPlan() {
    if (!prd) return
    if (
      !window.confirm(
        'Apply this plan? It creates real Epics/Sprints/Tasks in Delivery — this cannot be undone from here.',
      )
    ) {
      return
    }
    const data = await callEndpoint('/api/z2h/prd/apply-plan', { prdId: prd.id })
    if (data) {
      const counts = `created ${data.created.epics.length} epic${data.created.epics.length === 1 ? '' : 's'}, ${data.created.sprints.length} sprint${data.created.sprints.length === 1 ? '' : 's'}, ${data.created.tasks.length} task${data.created.tasks.length === 1 ? '' : 's'}`
      if (data.partial) {
        setActionMessage({
          ok: false,
          text: `Partially applied — ${counts}; ${data.failures.length} item${data.failures.length === 1 ? '' : 's'} failed (listed below). Fix the cause, then Apply plan again — already-created items are skipped.`,
        })
      } else {
        setActionMessage({ ok: true, text: `Applied — ${counts}.` })
      }
      await loadState()
    }
  }

  const status = prd?.status || null
  // UNANSWERED gaps only (Task 4 carry-over fix) — redraft now preserves
  // answered gaps (see generate.ts's merge), so gaps.length overstates open
  // questions once any have been answered.
  const gapCount = Array.isArray(prd?.gaps) ? prd.gaps.filter((g) => !g.answer).length : 0
  const planUnapplied = isPlanUnapplied(prd)
  const plan =
    prd?.proposedPlan && typeof prd.proposedPlan === 'object' && !Array.isArray(prd.proposedPlan)
      ? prd.proposedPlan
      : null
  // A partial apply (some items failed to create — see the applied envelope
  // the server stamps into proposedPlan) re-opens Apply: the server resumes
  // the same proposal, creating only the missing docs.
  const partialFailures = Array.isArray(plan?.applied?.failures) ? plan.applied.failures : []
  const isPartiallyApplied = partialFailures.length > 0

  const canGenerate = !prd || status === 'approved'
  const canRegenerate = Boolean(prd) && status !== 'approved'
  const canSendIntake = Boolean(prd) && status === 'gaps-pending'
  const canShare = Boolean(prd) && (status === 'internal-review' || status === 'changes-requested')
  const canPropose = Boolean(prd) && status === 'approved'
  const canApply = Boolean(prd) && (planUnapplied || isPartiallyApplied)
  const canViewPortal = Boolean(prd) && portalToken && PORTAL_VISIBLE_STATUSES.includes(status)

  // Admin intake controls (Task 4). "Intake exists" = a token was ever
  // issued (Send intake ran at least once) — Reopen/Reset on a PRD that
  // never had an intake sent has nothing to reopen/reset. Approved PRDs are
  // frozen for all three (matches the server-side claim WHERE clauses).
  const intakeExists = Boolean(prd?.intake?.token)
  const canReopen = intakeExists && status !== 'approved'
  const canReset = intakeExists && status !== 'approved'
  const canLock =
    Boolean(prd) &&
    prd?.intake?.status === 'complete' &&
    !prd?.intake?.locked &&
    (status === 'gaps-pending' || status === 'internal-review')

  const busy = Boolean(actionBusy)
  const importInFlight = Boolean(importBusy)

  const btn = (extra = {}) => ({
    padding: '8px 16px',
    borderRadius: 8,
    border: '1px solid var(--theme-elevation-250)',
    background: 'var(--theme-elevation-100)',
    color: 'var(--theme-text)',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    ...extra,
  })

  const primaryBtn = btn({
    border: 'none',
    background: 'var(--theme-elevation-800)',
    color: 'var(--theme-elevation-0)',
    fontWeight: 600,
  })

  const disabledStyle = { opacity: 0.5, cursor: 'default' }

  return (
    <div
      style={{
        border: '1px solid var(--theme-elevation-150)',
        borderRadius: 12,
        padding: '20px 24px',
        marginBottom: 24,
        background: 'var(--theme-elevation-50)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
        <h3 style={{ margin: 0, fontSize: 15 }}>Product requirements doc</h3>
        <button
          type="button"
          onClick={loadState}
          disabled={loading}
          style={{
            border: 'none',
            background: 'none',
            color: 'var(--theme-elevation-500)',
            fontSize: 12,
            cursor: loading ? 'default' : 'pointer',
          }}
        >
          {loading ? 'Refreshing…' : '↻ Refresh'}
        </button>
      </div>

      {loadError && (
        <p style={{ margin: '10px 0 0', fontSize: 13, color: 'var(--theme-error-500, #c62828)' }}>
          {loadError}
        </p>
      )}

      {!loading && !loadError && (
        <p style={{ margin: '6px 0 4px', fontSize: 13, color: 'var(--theme-elevation-600)' }}>
          {prd ? (
            <>
              v{prd.version} —{' '}
              <strong style={{ color: STATUS_COLOR[status] || 'inherit' }}>
                {STATUS_LABEL[status] || status}
              </strong>
              {gapCount > 0 ? ` — ${gapCount} gap${gapCount === 1 ? '' : 's'}` : ''}
            </>
          ) : (
            'No PRD generated yet for this project.'
          )}
        </p>
      )}

      {!loading && !loadError && prd && (
        <p style={{ margin: '0 0 16px', fontSize: 12.5, color: 'var(--theme-elevation-500)' }}>
          {intakeStatusLine(prd)}
        </p>
      )}

      {!loading && !loadError && submissions.length > 0 && (
        <div
          style={{
            marginBottom: 16,
            padding: '12px 14px',
            borderRadius: 10,
            border: '1px solid var(--theme-warning-400, #c98a1b)',
            background: 'var(--theme-warning-50, #fdf4e3)',
          }}
        >
          <p style={{ margin: '0 0 10px', fontSize: 13, fontWeight: 600 }}>
            Client submitted a revised document
            {submissions.length > 1 ? ` (${submissions.length})` : ''}
          </p>
          {submissions.map((submission) => (
            <div
              key={submission.id}
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
                padding: '6px 0',
              }}
            >
              <span style={{ fontSize: 12.5 }}>
                {submission.note || 'Revised PRD document'}
                {submission.createdAt ? ` — ${new Date(submission.createdAt).toLocaleDateString()}` : ''}
              </span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  onClick={() => reviewSubmission(submission)}
                  disabled={busy || importInFlight || submissionBusyId === submission.id}
                  style={
                    !busy && !importInFlight && submissionBusyId !== submission.id ? btn() : btn(disabledStyle)
                  }
                >
                  Review & import
                </button>
                <button
                  type="button"
                  onClick={() => dismissSubmission(submission)}
                  disabled={busy || importInFlight || submissionBusyId === submission.id}
                  style={
                    !busy && !importInFlight && submissionBusyId !== submission.id ? btn() : btn(disabledStyle)
                  }
                >
                  {submissionBusyId === submission.id ? 'Dismissing…' : 'Dismiss'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
        <button
          type="button"
          onClick={() => generate(false)}
          disabled={!canGenerate || busy}
          style={canGenerate && !busy ? primaryBtn : { ...primaryBtn, ...disabledStyle }}
        >
          {actionBusy === '/api/z2h/prd/generate' && !prd
            ? 'Generating…'
            : prd && status === 'approved'
              ? 'Generate new version'
              : 'Generate'}
        </button>

        <button
          type="button"
          onClick={() => generate(true)}
          disabled={!canRegenerate || busy}
          style={canRegenerate && !busy ? btn() : btn(disabledStyle)}
          title={status === 'approved' ? 'Approved PRDs are frozen — use Generate new version instead.' : undefined}
        >
          {actionBusy === '/api/z2h/prd/generate' && prd ? 'Regenerating…' : 'Regenerate'}
        </button>

        <input
          ref={importFileRef}
          type="file"
          accept=".md,.markdown,.txt,.docx"
          style={{ display: 'none' }}
          onChange={handleImportFile}
        />
        <button
          type="button"
          onClick={openImportPicker}
          disabled={busy || importInFlight}
          style={!busy && !importInFlight ? btn() : btn(disabledStyle)}
          title="Upload your own PRD as markdown or Word (.docx) — AI maps it onto the ten sections for you to review before anything is saved."
        >
          Import PRD
        </button>

        <button
          type="button"
          onClick={sendIntake}
          disabled={!canSendIntake || busy}
          style={canSendIntake && !busy ? btn() : btn(disabledStyle)}
          title="Emails the founder a link to answer the open gaps."
        >
          {actionBusy === '/api/z2h/prd/intake/send' ? 'Sending…' : 'Send intake'}
        </button>

        <button
          type="button"
          onClick={shareWithFounder}
          disabled={!canShare || busy}
          style={canShare && !busy ? btn() : btn(disabledStyle)}
          title="Shares the current draft with the founder for review."
        >
          {actionBusy === '/api/z2h/prd/share' ? 'Sharing…' : 'Share with founder'}
        </button>

        <button
          type="button"
          onClick={reopenIntake}
          disabled={!canReopen || busy}
          style={canReopen && !busy ? btn() : btn(disabledStyle)}
          title="Reopens the intake for the founder — answers are kept, only the lock is lifted."
        >
          {actionBusy === '/api/z2h/prd/intake/reopen' ? 'Reopening…' : 'Reopen'}
        </button>

        <button
          type="button"
          onClick={resetIntake}
          disabled={!canReset || busy}
          style={canReset && !busy ? btn() : btn(disabledStyle)}
          title="Clears every founder answer and starts the intake over from scratch."
        >
          {actionBusy === '/api/z2h/prd/intake/reset' ? 'Resetting…' : 'Reset'}
        </button>

        <button
          type="button"
          onClick={lockIntake}
          disabled={!canLock || busy}
          style={canLock && !busy ? btn() : btn(disabledStyle)}
          title="Locks the intake — the founder can no longer revise their answers until reopened."
        >
          {actionBusy === '/api/z2h/prd/intake/lock' ? 'Locking…' : 'Lock'}
        </button>

        <button
          type="button"
          onClick={proposePlan}
          disabled={!canPropose || busy}
          style={canPropose && !busy ? btn() : btn(disabledStyle)}
          title="Maps the approved PRD into epics/sprints/tasks for review before applying."
        >
          {actionBusy === '/api/z2h/prd/propose-plan'
            ? 'Proposing…'
            : plan
              ? 'Re-propose'
              : 'Propose plan'}
        </button>

        <button
          type="button"
          onClick={applyPlan}
          disabled={!canApply || busy}
          style={canApply && !busy ? primaryBtn : { ...primaryBtn, ...disabledStyle }}
          title="Creates the epics/sprints/tasks from the proposed plan."
        >
          {actionBusy === '/api/z2h/prd/apply-plan'
            ? 'Applying…'
            : isPartiallyApplied
              ? 'Retry apply'
              : 'Apply plan'}
        </button>

        {canViewPortal ? (
          <a
            href={`/status/${portalToken}/prd`}
            target="_blank"
            rel="noreferrer"
            style={{ ...btn(), textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}
          >
            View portal page
          </a>
        ) : null}

        {prd ? (
          <a
            href={`/api/z2h/prd/${prd.id}/doc`}
            style={{ ...btn(), textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}
            title="Downloads a branded .docx of the current PRD — any state, including a still-drafting skeleton."
          >
            Download .docx
          </a>
        ) : null}
      </div>

      {importOpen && (
        <div
          style={{
            marginTop: 16,
            paddingTop: 16,
            borderTop: '1px solid var(--theme-elevation-150)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 10 }}>
            <h4 style={{ margin: 0, fontSize: 13, fontWeight: 600 }}>
              Import: {importFileName || '(no file)'}
            </h4>
            <button
              type="button"
              onClick={cancelImport}
              disabled={importInFlight}
              style={{
                border: 'none',
                background: 'none',
                color: 'var(--theme-elevation-500)',
                fontSize: 12,
                cursor: importInFlight ? 'default' : 'pointer',
              }}
            >
              Cancel
            </button>
          </div>

          {importBusy === 'preview' && (
            <p style={{ margin: 0, fontSize: 13, color: 'var(--theme-elevation-500)' }}>Mapping your document…</p>
          )}

          {importError && (
            <p style={{ margin: '0 0 10px', fontSize: 13, color: 'var(--theme-error-500, #c62828)' }}>
              {importError}
            </p>
          )}

          {importPreview && (
            <>
              <div style={{ marginBottom: 14 }}>
                {(importPreview.mapping.sections || []).length === 0 ? (
                  <p style={{ margin: 0, fontSize: 13, color: 'var(--theme-elevation-500)' }}>
                    No canonical sections were mapped from this document.
                  </p>
                ) : (
                  (importPreview.mapping.sections || []).map((s, si) => (
                    <div
                      key={si}
                      style={{
                        marginBottom: 8,
                        padding: '8px 10px',
                        borderRadius: 8,
                        background: 'var(--theme-elevation-0)',
                        border: '1px solid var(--theme-elevation-150)',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 13, fontWeight: 600 }}>
                          {SECTION_LABELS[s.key] || s.key}
                        </span>
                        <span
                          style={{
                            fontSize: 10,
                            fontWeight: 600,
                            padding: '1px 6px',
                            borderRadius: 8,
                            background: 'var(--theme-elevation-100)',
                            color: CONFIDENCE_COLOR[s.confidence] || 'var(--theme-elevation-600)',
                          }}
                        >
                          {s.confidence}
                        </span>
                      </div>
                      <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--theme-elevation-600)' }}>
                        {(s.sourceHeadings || []).length
                          ? `from: ${s.sourceHeadings.join(', ')}`
                          : '(no source heading — synthesized from unheaded prose)'}
                      </p>
                    </div>
                  ))
                )}
              </div>

              {(importPreview.mapping.unmapped || []).length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <h5 style={{ margin: '0 0 6px', fontSize: 12, fontWeight: 600, color: 'var(--theme-elevation-600)' }}>
                    Not mapped ({importPreview.mapping.unmapped.length})
                  </h5>
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {importPreview.mapping.unmapped.map((u, ui) => (
                      <li key={ui} style={{ fontSize: 12.5, marginBottom: 4 }}>
                        <strong>{u.heading}</strong> — {u.summary}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {importEffectiveRunGaps && (importPreview.mapping.gaps || []).length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <h5 style={{ margin: '0 0 6px', fontSize: 12, fontWeight: 600, color: 'var(--theme-elevation-600)' }}>
                    Gaps ({importPreview.mapping.gaps.length})
                  </h5>
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {importPreview.mapping.gaps.map((g, gi) => (
                      <li key={gi} style={{ fontSize: 12.5, marginBottom: 4 }}>
                        {g.question}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16, marginBottom: 14 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                  <input
                    type="radio"
                    name="import-lifecycle"
                    value="internal-review"
                    checked={importLifecycle === 'internal-review'}
                    onChange={() => setImportLifecycle('internal-review')}
                  />
                  Draft for review
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                  <input
                    type="radio"
                    name="import-lifecycle"
                    value="approved"
                    checked={importLifecycle === 'approved'}
                    onChange={() => setImportLifecycle('approved')}
                  />
                  Approved (final)
                </label>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    fontSize: 13,
                    opacity: importLifecycle === 'approved' ? 0.5 : 1,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={importEffectiveRunGaps}
                    disabled={importLifecycle === 'approved'}
                    onChange={(e) => setImportGapsWanted(e.target.checked)}
                  />
                  Include gap analysis
                </label>
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  type="button"
                  onClick={confirmImport}
                  disabled={importInFlight}
                  style={!importInFlight ? primaryBtn : { ...primaryBtn, ...disabledStyle }}
                >
                  {importBusy === 'confirm' ? 'Confirming…' : 'Confirm import'}
                </button>
                <button type="button" onClick={cancelImport} disabled={importInFlight} style={importInFlight ? btn(disabledStyle) : btn()}>
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {actionMessage && (
        <p
          style={{
            margin: '12px 0 0',
            fontSize: 13,
            color: actionMessage.ok
              ? 'var(--theme-success-500, #2e7d32)'
              : 'var(--theme-error-500, #c62828)',
          }}
        >
          {actionMessage.text}
        </p>
      )}

      {plan && (
        <div
          style={{
            marginTop: 16,
            paddingTop: 16,
            borderTop: '1px solid var(--theme-elevation-150)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <h4 style={{ margin: 0, fontSize: 13, fontWeight: 600 }}>
              Proposed plan
            </h4>
            {plan.mode === 'delta' && (
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  padding: '1px 8px',
                  borderRadius: 10,
                  background: 'var(--theme-elevation-800)',
                  color: 'var(--theme-elevation-0)',
                }}
              >
                delta
              </span>
            )}
            <span
              style={{
                fontSize: 12,
                color: isPartiallyApplied
                  ? 'var(--theme-warning-500, #b26a00)'
                  : 'var(--theme-elevation-500)',
                fontWeight: isPartiallyApplied ? 600 : 400,
              }}
            >
              {isPartiallyApplied
                ? `Partially applied — ${partialFailures.length} item${partialFailures.length === 1 ? '' : 's'} failed`
                : planUnapplied
                  ? 'not yet applied'
                  : 'applied'}
            </span>
          </div>

          {isPartiallyApplied && (
            <div
              style={{
                marginBottom: 10,
                padding: '8px 12px',
                borderRadius: 8,
                border: '1px solid var(--theme-warning-400, #c98a1b)',
                background: 'var(--theme-warning-50, #fdf4e3)',
              }}
            >
              <p style={{ margin: 0, fontSize: 12.5 }}>
                These items failed to create. Fix the cause, then Apply plan again — items that
                already exist are skipped, only the missing ones are created.
              </p>
              <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                {partialFailures.map((f, fi) => (
                  <li key={fi} style={{ fontSize: 12, marginBottom: 2 }}>
                    <code style={{ fontSize: 11 }}>{f.path}</code>: {f.error}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {(plan.epics || []).length === 0 ? (
            <p style={{ margin: 0, fontSize: 13, color: 'var(--theme-elevation-500)' }}>
              {plan.mode === 'delta'
                ? 'No new additions proposed — the applied plan already covers the PRD.'
                : 'No epics proposed.'}
            </p>
          ) : (
            (plan.epics || []).map((epic, ei) => (
              <div key={ei} style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{epic.name}</div>
                {(epic.sprints || []).map((sprint, si) => (
                  <div key={si} style={{ marginLeft: 16, marginTop: 4 }}>
                    <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--theme-elevation-600)' }}>
                      {sprint.name}
                    </div>
                    <ul style={{ margin: '2px 0 0', paddingLeft: 20 }}>
                      {(sprint.tasks || []).map((task, ti) => (
                        <li key={ti} style={{ fontSize: 12.5, marginBottom: 2 }}>
                          {task.title}{' '}
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 600,
                              padding: '1px 6px',
                              borderRadius: 8,
                              background: 'var(--theme-elevation-100)',
                              color: 'var(--theme-elevation-600)',
                            }}
                          >
                            {SECTION_LABELS[task.prdSection] || task.prdSection}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  )
}
